import type { RolDeUsuario } from '../servicios/api'

/**
 * TODOS los textos de los TIPs (RF-1703 a RF-1706), en un solo lugar para que el contador del piloto los
 * revise de una vez y para que nadie los riegue por el JSX (RN-67: viven en el código, junto a la función que
 * explican, y cambiarlos exige desplegar).
 *
 * Cómo se escriben: tutean, en imperativo, cortos, y dicen qué hacer. RN-65: un TIP nunca enseña una acción que
 * el rol no puede hacer; por eso varias pantallas tienen una versión por rol. `roles` sin definir quiere decir
 * «todos los roles». Administrador y contador ven lo mismo (decisión del propietario, 21/10/2026).
 *
 * Las claves son las que se guardan en el servidor como «ya visto» (`[a-z0-9-]`, hasta 60). Cambiar la clave de
 * un TIP lo vuelve a mostrar a todos: hazlo solo si su contenido cambió de verdad.
 */

export interface TipDePantallaContenido {
  clave: string
  /** Patrón de react-router de la pantalla, tal como está en `App.tsx`. */
  ruta: string
  titulo: string
  texto: string
  roles?: RolDeUsuario[]
}

export interface AyudaDeCampoContenido {
  titulo: string
  texto: string
}

export interface TipDelDiaContenido {
  clave: string
  texto: string
  roles?: RolDeUsuario[]
}

export interface NovedadContenido {
  clave: string
  /** Fecha local del despliegue (yyyy-MM-dd). No se muestra a quien se dio de alta ese día o después. */
  fecha: string
  titulo: string
  texto: string
  /** Ruta a la que lleva el enlace. `:id` se sustituye por el cliente con que se está trabajando. */
  ruta?: string
  /** Texto del enlace; sin ruta no hay enlace. */
  enlace?: string
  roles?: RolDeUsuario[]
}

/** Registran, sellan, cierran y presentan: los que pueden cambiar la contabilidad de fondo. */
const ESCRIBEN: RolDeUsuario[] = ['Administrador', 'Contador']
/** Además capturan pólizas e importan CFDI: el auxiliar también. */
const CAPTURAN: RolDeUsuario[] = ['Administrador', 'Contador', 'Auxiliar']
const AUXILIAR: RolDeUsuario[] = ['Auxiliar']
const SOLO_LECTURA: RolDeUsuario[] = ['SoloLectura']
const SIN_ESCRITURA: RolDeUsuario[] = ['Auxiliar', 'SoloLectura']

/** Un elemento sin `roles` aplica a todos; con `roles`, solo a esos. Sin rol conocido, solo lo de todos. */
export function aplicaAlRol(roles: RolDeUsuario[] | undefined, rol: RolDeUsuario | null): boolean {
  return roles === undefined || (rol !== null && roles.includes(rol))
}

// ------------------------------------------------------------------- RF-1703 · TIP al entrar a una pantalla

export const TIPS_DE_PANTALLA: TipDePantallaContenido[] = [
  {
    clave: 'pantalla-tablero',
    ruta: '/',
    titulo: 'Tu día empieza aquí',
    texto:
      'El semáforo de cada cliente junta sus obligaciones: balanza electrónica, pago provisional y balanza ajustada. ' +
      'Atiende primero lo que está en rojo; cada fila te lleva a la pantalla donde se resuelve.',
  },
  {
    clave: 'pantalla-clientes',
    ruta: '/clientes',
    titulo: 'Tu cartera',
    texto: 'Aquí están los clientes que atiendes. Abre uno para trabajar en su expediente.',
  },
  {
    clave: 'pantalla-expediente',
    ruta: '/clientes/:id',
    titulo: 'El expediente del cliente',
    texto:
      'Las pestañas de arriba son todo lo que llevas de este cliente: CFDI, pólizas, reportes, envíos al SAT y ' +
      'declaraciones. Revisa aquí también sus datos fiscales.',
  },
  {
    clave: 'pantalla-cfdi',
    ruta: '/clientes/:id/cfdi',
    titulo: 'Trae los CFDI del SAT',
    texto:
      'Usa «Descargar por rango» para pedirle al SAT los comprobantes del cliente; necesita su e.firma vigente. ' +
      'La descarga corre en segundo plano y aquí ves su avance. «Importar XML/ZIP» es para lo que la descarga no alcance.',
    roles: ESCRIBEN,
  },
  {
    clave: 'pantalla-cfdi-auxiliar',
    ruta: '/clientes/:id/cfdi',
    titulo: 'Los CFDI del cliente',
    texto:
      'Busca por fecha, tipo o texto y abre cada comprobante para ver su póliza. Puedes importar XML o ZIP; la ' +
      'descarga masiva del SAT la pide un contador o un administrador.',
    roles: AUXILIAR,
  },
  {
    clave: 'pantalla-cfdi-lectura',
    ruta: '/clientes/:id/cfdi',
    titulo: 'Los CFDI del cliente',
    texto: 'Busca por fecha, tipo o texto y abre cada comprobante para ver su póliza.',
    roles: SOLO_LECTURA,
  },
  {
    clave: 'pantalla-contabilizacion',
    ruta: '/clientes/:id/contabilizacion',
    titulo: 'Contabiliza desde la cola',
    texto:
      'A la izquierda están los CFDI del periodo que esperan póliza. Elige uno: ContaQuick te propone la póliza; ' +
      'revísala y regístrala, o captúrala a mano. Con «Reglas…» dejas dichas las cuentas de un emisor para la próxima vez.',
    roles: ESCRIBEN,
  },
  {
    clave: 'pantalla-contabilizacion-auxiliar',
    ruta: '/clientes/:id/contabilizacion',
    titulo: 'Contabiliza desde la cola',
    texto:
      'A la izquierda están los CFDI del periodo que esperan póliza. Elige uno: ContaQuick te propone la póliza; ' +
      'revísala y regístrala, o captúrala a mano.',
    roles: AUXILIAR,
  },
  {
    clave: 'pantalla-contabilizacion-lectura',
    ruta: '/clientes/:id/contabilizacion',
    titulo: 'Lo que falta por contabilizar',
    texto: 'Aquí ves qué CFDI del periodo siguen sin póliza. Cambia el periodo arriba.',
    roles: SOLO_LECTURA,
  },
  {
    clave: 'pantalla-catalogo',
    ruta: '/clientes/:id/catalogo',
    titulo: 'El catálogo y su código agrupador',
    texto:
      'Si el catálogo está vacío, genéralo desde la plantilla de su régimen y ajústalo. Cada cuenta de registro ' +
      'necesita su código agrupador del SAT para poder generar el catálogo (CT); el «?» junto al campo te dice cómo elegirlo.',
    roles: ESCRIBEN,
  },
  {
    clave: 'pantalla-catalogo-consulta',
    ruta: '/clientes/:id/catalogo',
    titulo: 'El catálogo de cuentas',
    texto: 'Consulta las cuentas del cliente y el código agrupador del SAT de cada una.',
    roles: SIN_ESCRITURA,
  },
  {
    clave: 'pantalla-polizas',
    ruta: '/clientes/:id/polizas',
    titulo: 'Registra pólizas',
    texto:
      '«Nueva póliza» abre la captura: cargos y abonos tienen que cuadrar. Al terminar el mes, ciérralo con «Cerrar ' +
      'periodo»; solo un administrador lo reabre.',
    roles: ESCRIBEN,
  },
  {
    clave: 'pantalla-polizas-auxiliar',
    ruta: '/clientes/:id/polizas',
    titulo: 'Registra pólizas',
    texto:
      '«Nueva póliza» abre la captura: cargos y abonos tienen que cuadrar. Corregir, cancelar y cerrar el periodo lo ' +
      'hace un contador o un administrador.',
    roles: AUXILIAR,
  },
  {
    clave: 'pantalla-polizas-lectura',
    ruta: '/clientes/:id/polizas',
    titulo: 'Las pólizas del periodo',
    texto: 'Elige el mes arriba y abre cada póliza para ver sus movimientos.',
    roles: SOLO_LECTURA,
  },
  {
    clave: 'pantalla-plantillas-poliza',
    ruta: '/clientes/:id/plantillas-poliza',
    titulo: 'Pólizas que se repiten',
    texto:
      'Guarda aquí lo que registras cada mes: depreciación, rentas, provisiones. Cada mes ContaQuick genera una póliza ' +
      'pendiente para que la revises; nunca contabiliza sola.',
    roles: ESCRIBEN,
  },
  {
    clave: 'pantalla-reportes',
    ruta: '/clientes/:id/reportes',
    titulo: 'Los reportes del periodo',
    texto:
      'Cambia de pestaña para ver balanza de comprobación, libro mayor, estado de resultados, balance general y libro ' +
      'diario. Cada uno se exporta a Excel o PDF.',
  },
  {
    clave: 'pantalla-control-envios',
    ruta: '/clientes/:id/control-envios',
    titulo: 'La contabilidad electrónica al SAT',
    texto:
      'Genera aquí la balanza y el catálogo en XML, ya validados contra el esquema del SAT. Súbelos tú al Buzón ' +
      'Tributario con la e.firma del cliente; después márcalos como enviados y registra el acuse. «Cómo se presenta» ' +
      'te dice qué subir y dónde.',
    roles: ESCRIBEN,
  },
  {
    clave: 'pantalla-control-envios-consulta',
    ruta: '/clientes/:id/control-envios',
    titulo: 'La contabilidad electrónica al SAT',
    texto: 'Consulta qué archivos del Anexo 24 se generaron, cuáles se enviaron y cuáles ya tienen acuse.',
    roles: SIN_ESCRITURA,
  },
  {
    clave: 'pantalla-balanza-ajustada',
    ruta: '/clientes/:id/balanza-ajustada',
    titulo: 'La balanza del cierre',
    texto: 'Es la balanza de diciembre más los ajustes del cierre del ejercicio. Elige el ejercicio arriba.',
  },
  {
    clave: 'pantalla-iva',
    ruta: '/clientes/:id/iva',
    titulo: 'El papel de trabajo de IVA',
    texto:
      'El borrador cambia en cuanto contabilizas o cancelas un CFDI. Séllalo cuando vayas a declarar: el sello lo ' +
      'congela con el origen de cada cifra.',
    roles: ESCRIBEN,
  },
  {
    clave: 'pantalla-iva-consulta',
    ruta: '/clientes/:id/iva',
    titulo: 'El papel de trabajo de IVA',
    texto: 'Cada cifra dice de dónde sale. Los papeles sellados son los que se llevaron a la declaración.',
    roles: SIN_ESCRITURA,
  },
  {
    clave: 'pantalla-isr',
    ruta: '/clientes/:id/isr',
    titulo: 'El ISR provisional',
    texto:
      'ContaQuick lo calcula a las personas morales del régimen general (601). Captura primero los parámetros del ' +
      'ejercicio —coeficiente de utilidad, PTU y pérdidas— y después sella el papel del mes.',
    roles: ESCRIBEN,
  },
  {
    clave: 'pantalla-isr-consulta',
    ruta: '/clientes/:id/isr',
    titulo: 'El ISR provisional',
    texto: 'ContaQuick lo calcula a las personas morales del régimen general (601). Cada cifra dice de dónde sale.',
    roles: SIN_ESCRITURA,
  },
  {
    clave: 'pantalla-declaraciones',
    ruta: '/clientes/:id/declaraciones',
    titulo: 'Registra lo que presentaste',
    texto:
      'Cuando presentes una declaración en el portal del SAT, regístrala aquí con su fecha y su número de operación, ' +
      'y resguarda el acuse. ContaQuick no presenta nada por ti; con el registro, el tablero la da por cumplida.',
    roles: ESCRIBEN,
  },
  {
    clave: 'pantalla-declaraciones-consulta',
    ruta: '/clientes/:id/declaraciones',
    titulo: 'Las declaraciones del mes',
    texto: 'Consulta qué declaraciones del mes se presentaron y descarga sus acuses.',
    roles: SIN_ESCRITURA,
  },
  {
    clave: 'pantalla-conciliacion-cfdi',
    ruta: '/clientes/:id/conciliacion-cfdi',
    titulo: 'CFDI contra contabilidad',
    texto:
      'Compara los CFDI emitidos del mes contra las pólizas del mes y señala las diferencias. No las corrige: eso se ' +
      'hace con una póliza de ajuste en un periodo abierto.',
  },
  {
    clave: 'pantalla-conciliacion-bancaria',
    ruta: '/clientes/:id/conciliacion-bancaria',
    titulo: 'La conciliación bancaria',
    texto:
      'Da de alta la cuenta bancaria, importa el estado de cuenta y empareja cada movimiento con su póliza. Al ' +
      'terminar el mes, cierra la conciliación: la diferencia tiene que quedar explicada.',
    roles: ESCRIBEN,
  },
  {
    clave: 'pantalla-conciliacion-bancaria-consulta',
    ruta: '/clientes/:id/conciliacion-bancaria',
    titulo: 'La conciliación bancaria',
    texto: 'Consulta la conciliación del mes y las partidas pendientes, ordenadas por antigüedad.',
    roles: SIN_ESCRITURA,
  },
  {
    clave: 'pantalla-folios',
    ruta: '/clientes/:id/folios',
    titulo: 'Series y folios',
    texto: 'Cada comprobante que se timbre desde ContaQuick toma el siguiente folio de su serie: dos nunca comparten número.',
  },
  {
    clave: 'pantalla-cfdi-cartera',
    ruta: '/cfdi',
    titulo: 'Los CFDI de toda tu cartera',
    texto: 'Busca un comprobante en todos tus clientes a la vez. Para importar o descargar, entra al repositorio del cliente.',
  },
  {
    clave: 'pantalla-contabilidad-electronica',
    ruta: '/contabilidad-electronica',
    titulo: 'Qué le falta presentar a tu cartera',
    texto:
      'Elige el mes y ve, cliente por cliente, qué falta presentar: archivos del Anexo 24 y declaraciones, lo que falta ' +
      'generar y los acuses pendientes. Descarga desde aquí el paquete de cada cliente.',
  },
]

// ------------------------------------------------------------------------- RF-1704 · Ayuda junto al campo

export const AYUDAS_DE_CAMPO: Record<string, AyudaDeCampoContenido> = {
  'codigo-agrupador': {
    titulo: 'Código agrupador del SAT',
    texto:
      'Es la clave del Anexo 24 que dice a qué rubro del SAT corresponde la cuenta (por ejemplo, 102.01 «Bancos ' +
      'nacionales»). La lleva toda cuenta de registro y tiene que coincidir con su naturaleza, deudora o acreedora. ' +
      'Elige la clave cuyo nombre oficial dice lo mismo que tu cuenta: el selector muestra el nombre de la tabla oficial.',
  },
  'numero-de-operacion': {
    titulo: 'Número de operación',
    texto:
      'Es el número que el SAT le asigna a la declaración al recibirla. Viene en el acuse de recibo que descargas del ' +
      'portal. Cópialo tal cual: letras y dígitos, sin espacios ni guiones.',
  },
  'tipo-de-cambio': {
    titulo: 'Tipo de cambio',
    texto:
      'Si el movimiento es en otra moneda, captura el tipo de cambio que trae el comprobante. Los importes de la póliza ' +
      'van siempre en pesos; el tipo de cambio deja constancia de cómo se convirtieron.',
  },
  'coeficiente-de-utilidad': {
    titulo: 'Coeficiente de utilidad',
    texto:
      'Sale de la declaración anual: utilidad fiscal entre ingresos nominales del ejercicio (LISR art. 14). Usa el del ' +
      'último ejercicio con declaración presentada o vencida, con cuatro decimales, y anota de dónde lo tomaste.',
  },
  'ptu-pagada': {
    titulo: 'PTU pagada en el ejercicio',
    texto:
      'La participación de los trabajadores en las utilidades que el cliente pagó este ejercicio. Se resta de la ' +
      'utilidad del pago provisional en partes iguales de mayo a diciembre (LISR art. 14). Anota cuándo se pagó.',
  },
  'perdidas-fiscales': {
    titulo: 'Pérdidas fiscales pendientes',
    texto:
      'Las pérdidas de ejercicios anteriores que todavía no se aplican, una por ejercicio de origen, con lo que queda ' +
      'pendiente según la declaración anual. Se restan durante los diez ejercicios siguientes (LISR art. 57). ' +
      'ContaQuick todavía no las actualiza con el INPC.',
  },
  'tipo-de-solicitud': {
    titulo: 'Tipo de solicitud',
    texto:
      'Solo se pide cuando el SAT te solicita pólizas o auxiliares (PL, XF, XC). Elige el motivo: acto de ' +
      'fiscalización, fiscalización por compulsa, devolución o compensación. Para los dos primeros captura el número de ' +
      'orden; para devolución o compensación, el número de trámite. Los dos vienen en el documento del SAT.',
  },
  'fecha-de-modificacion-de-balanza': {
    titulo: 'Fecha de modificación de la balanza',
    texto: 'Solo en una balanza complementaria: la fecha en que modificaste la balanza que ya habías enviado.',
  },
}

// ------------------------------------------------------------------------- RF-1705 · TIP del día

export const TIPS_DEL_DIA: TipDelDiaContenido[] = [
  {
    clave: 'dia-semaforo',
    texto: 'Cada fila del tablero te lleva directo a la pantalla donde se resuelve esa obligación.',
  },
  {
    clave: 'dia-cartera-cfdi',
    texto: 'En «CFDI de la cartera» buscas un comprobante por UUID, RFC, folio o importe en todos tus clientes a la vez.',
  },
  {
    clave: 'dia-exportar',
    texto: 'Todos los reportes del periodo se exportan a Excel o PDF desde su propia pestaña.',
  },
  {
    clave: 'dia-congruencia',
    texto: 'La nota de congruencia en Reportes te avisa si la utilidad del estado de resultados no cuadra con el capital.',
  },
  {
    clave: 'dia-reglas',
    texto: 'En Contabilización, «Reglas…» recuerda las cuentas de un emisor: la próxima póliza de ese RFC sale casi lista.',
    roles: ESCRIBEN,
  },
  {
    clave: 'dia-plantillas',
    texto: 'Deja como plantilla las pólizas que se repiten cada mes, como la depreciación: ContaQuick te las prepara para que las revises.',
    roles: ESCRIBEN,
  },
  {
    clave: 'dia-paquete',
    texto: 'Desde el papel de IVA o el de ISR descargas el paquete de declaraciones del mes en un solo ZIP.',
  },
  {
    clave: 'dia-guia',
    texto: '«Cómo se presenta», en Control de envíos, te dice qué archivo subir al SAT, en qué apartado y hasta cuándo.',
  },
  {
    clave: 'dia-antiguedad',
    texto: 'Las partidas en conciliación bancaria se ordenan por antigüedad: las más viejas son las primeras que hay que explicar.',
  },
  {
    clave: 'dia-captura',
    texto: 'Captura las pólizas con los cargos y los abonos cuadrados: una póliza descuadrada no se registra.',
    roles: CAPTURAN,
  },
  {
    clave: 'dia-ayuda',
    texto: 'El menú «Ayuda» de arriba vuelve a lanzar el recorrido guiado cuando quieras, completo o por tema.',
  },
]

// ------------------------------------------------------------------------- RF-1706 · Novedades

export const NOVEDADES: NovedadContenido[] = [
  {
    clave: 'novedad-conciliacion-bancaria',
    fecha: '2026-10-19',
    titulo: 'Nuevo: conciliación bancaria',
    texto:
      'Importa el estado de cuenta, empareja cada movimiento con su póliza y cierra la conciliación del mes con las ' +
      'partidas pendientes por antigüedad.',
    ruta: '/clientes/:id/conciliacion-bancaria',
    enlace: 'Ir a la conciliación bancaria',
  },
  {
    clave: 'novedad-declaraciones',
    fecha: '2026-10-20',
    titulo: 'Nuevo: pestaña Declaraciones',
    texto:
      'Registra las declaraciones que presentaste con su número de operación y resguarda el acuse. El tablero ya da por ' +
      'cumplido el pago provisional cuando la declaración está registrada, no cuando el papel está sellado.',
    ruta: '/clientes/:id/declaraciones',
    enlace: 'Ir a Declaraciones',
  },
  {
    clave: 'novedad-plantillas-corregidas',
    fecha: '2026-10-21',
    titulo: 'Plantillas de catálogo corregidas',
    texto:
      'Las plantillas ahora ligan cada cuenta al código agrupador oficial del Anexo 24, y el selector muestra los nombres ' +
      'oficiales. Si generaste un catálogo antes, revisa sus agrupadores.',
    ruta: '/clientes/:id/catalogo',
    enlace: 'Ir al catálogo',
    roles: ['Contador'],
  },
  {
    clave: 'novedad-reiniciar-catalogo',
    fecha: '2026-10-21',
    titulo: 'Plantillas corregidas y «Reiniciar catálogo»',
    texto:
      'Las plantillas ahora ligan cada cuenta al código agrupador oficial del Anexo 24. Si un cliente tiene un catálogo ' +
      'que nadie ha usado (sin pólizas, reglas, plantillas ni cuentas bancarias), «Reiniciar catálogo» lo borra para ' +
      'que lo generes de nuevo con la plantilla corregida.',
    ruta: '/clientes/:id/catalogo',
    enlace: 'Ir al catálogo',
    roles: ['Administrador'],
  },
  {
    clave: 'novedad-paquete-de-presentacion',
    fecha: '2026-10-22',
    titulo: 'Nuevo: paquete de presentación del periodo',
    texto:
      'En Contabilidad electrónica ves qué le falta presentar a cada cliente del mes y descargas, en un solo ZIP, los ' +
      'archivos del Anexo 24 pendientes, los papeles de las declaraciones y la guía de cada uno. También está en la ' +
      'pestaña Declaraciones de cada cliente.',
    ruta: '/contabilidad-electronica',
    enlace: 'Ver qué me falta presentar',
  },
]
